/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Form, Tooltip } from '@mairistem/composants';
import {
  Section,
  SectionHeader,
  PrenomsInput,
  _string,
} from '@mairistem/administres-utils';

import { DATE_FORMAT } from '@/constants';

import {
  ChampAdresse,
  ChampProfession,
  STYLE_TITRE_SECTION,
} from '@/components/Common';

interface InformationsDeclarantProps {
  /**
   * Données COMPLÈTES de l'assistant, et non le seul bloc `declarant` : le
   * chemin d'écriture du domicile (`declarant.adresseDomicile`) est résolu par
   * `ChampLieu` depuis la racine.
   */
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
}

/** Préfixe des `name` : le déclarant est un sous-objet, comme `parentN`. */
const PREFIXE = 'declarant.';

/**
 * Bloc « INFORMATIONS SUR LE DÉCLARANT ».
 *
 * ⚠️ Le déclarant n'a **pas de date de naissance** : la base la laisse vide et
 * porte un ÂGE (`ai_age`). D'où une saisie propre plutôt que le composant
 * `EtatCivil` de la lib, qui exigerait une date de naissance et un sexe — ce
 * dernier n'étant pas renseigné pour un déclarant.
 *
 * Le domicile est saisi mais non obligatoire : la source est l'acte papier.
 */
const InformationsDeclarant = (props: InformationsDeclarantProps) => {
  const { data, errors = {}, onChange } = props;

  const declarant = data?.declarant ?? {};

  /**
   * Validité de la commune du domicile à la date du DÉCÈS, pas à la date de
   * naissance du défunt — que `ChampLieu` prendrait par défaut, `data` portant
   * l'état civil du défunt à plat.
   */
  const dateValiditeDomicile = !_.isEmpty(data?.dateDeces)
    ? dayjs(data.dateDeces, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  const handleNomChange = React.useCallback(
    (
      event: React.SyntheticEvent | null,
      payload: { name: string; value: string },
    ) => {
      onChange(event, {
        name: payload.name,
        value: _string.normalizeNomFamille(payload.value),
      });
    },
    [onChange],
  );

  const handleAgeChange = React.useCallback(
    (
      event: React.SyntheticEvent | null,
      payload: { name: string; value: string },
    ) => {
      // `ai_age` est un entier : on n'accepte que des chiffres, comme le
      // numéro d'acte d'`InformationsActe`.
      onChange(event, {
        name: payload.name,
        value: payload.value.replace(/[^0-9]/g, ''),
      });
    },
    [onChange],
  );

  return (
    <Section title="INFORMATIONS SUR LE DÉCLARANT" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        {/* @ts-ignore */}
        <SectionHeader title="Identité" required style={STYLE_TITRE_SECTION} />
        <div style={{ padding: '1em' }}>
          {/*
            ⚠️ Largeurs EXPLICITES, et pas `widths="equal"` sur le groupe :
            la règle CSS de Semantic (`.equal.width.fields > .field`) ne vise
            que les enfants DIRECTS qui sont des `.field`. Un `Tooltip`
            intercalé n'en est pas un, si bien que le champ qu'il enveloppe
            gardait sa largeur par défaut alors que son voisin nu passait à la
            moitié — d'où une ligne mal répartie et visuellement plus serrée
            que celle des prénoms juste au-dessus.

            Les largeurs sur seize alignent la ligne sur celle des prénoms
            (trois colonnes) : l'âge sous « Prénom usuel », la profession sur
            les deux colonnes suivantes.
          */}
          <Form.Group>
            <Tooltip
              trigger={
                <Form.Input
                  width={16}
                  required
                  label="Nom"
                  name={`${PREFIXE}nomFamille`}
                  value={declarant?.nomFamille ?? ''}
                  error={!_.isNil(errors?.nomFamille)}
                  onChange={handleNomChange}
                />
              }
              type={errors?.nomFamille ? 'error' : ''}
              message={errors?.nomFamille}
            />
          </Form.Group>
          <PrenomsInput
            attributePrefix={PREFIXE}
            prenom={declarant?.prenom ?? ''}
            prenomsComplementaires={declarant?.prenomsComplementaires ?? ''}
            errors={errors}
            onChange={onChange}
          />
          {/*
            `marginTop` explicite : `PrenomsInput` met `marginBottom: 0` sur son
            propre groupe dans le cas courant, et la ligne suivante s'y collait.
            Compensé ici plutôt que dans la lib, partagée (cf. `CLAUDE.md`).
          */}
          <Form.Group style={{ marginTop: '1em' }}>
            <Tooltip
              trigger={
                <Form.Input
                  width={5}
                  label="Âge"
                  name={`${PREFIXE}age`}
                  maxLength={3}
                  value={_.toString(declarant?.age ?? '')}
                  error={!_.isNil(errors?.age)}
                  onChange={handleAgeChange}
                />
              }
              type={errors?.age ? 'error' : ''}
              message={errors?.age}
            />
            <ChampProfession
              width={11}
              name={`${PREFIXE}profession`}
              value={declarant?.profession}
              errors={errors}
              cleErreur="profession"
              onChange={onChange}
            />
          </Form.Group>
        </div>
        <ChampAdresse
          data={data}
          errors={errors}
          onChange={onChange}
          name={`${PREFIXE}adresseDomicile`}
          title="Adresse de domicile"
          dateValidite={dateValiditeDomicile}
        />
      </Form>
    </Section>
  );
};

export default InformationsDeclarant;
